import { type ForwardRefExoticComponent, Fragment, forwardRef } from 'react';

import { AssistChipAvatar } from './assist-chip-avatar';
import { AssistChipIcon } from './assist-chip-icon';
import { AssistChipLabel } from './assist-chip-label';
import { AssistChipLoading } from './assist-chip-loading';
import { AssistChipRoot } from './assist-chip-root';
import type { AssistChipPropsInterface } from './types/assist-chip.types';

/**
 * Assist chips are used to trigger actions related to pieces of content.
 * Avoid using them as CTAs or for navigation (ex: as tabs).
 *
 * @related ['Button', 'FilterChip', 'InputChip']
 * @since 8.97.0
 */

export const AssistChip = forwardRef<HTMLButtonElement, AssistChipPropsInterface>(
  (props: AssistChipPropsInterface, forwardedRef) => {
    const { avatar, children, icon, loading, truncate, ...rest } = props;

    return (
      <AssistChip.Root ref={forwardedRef} loading={loading} {...rest}>
        {loading ? (
          <AssistChip.Loading />
        ) : (
          <Fragment>
            {avatar ? <AssistChip.Avatar>{avatar}</AssistChip.Avatar> : null}
            {icon ? <AssistChip.Icon>{icon}</AssistChip.Icon> : null}
          </Fragment>
        )}
        <AssistChip.Label truncate={truncate}>{String(children)}</AssistChip.Label>
      </AssistChip.Root>
    );
  },
) as ForwardRefExoticComponent<AssistChipPropsInterface> & {
  Root: typeof AssistChipRoot;
  Avatar: typeof AssistChipAvatar;
  Icon: typeof AssistChipIcon;
  Label: typeof AssistChipLabel;
  Loading: typeof AssistChipLoading;
};

AssistChip.displayName = 'AssistChip';

AssistChip.Root = AssistChipRoot;
AssistChip.Avatar = AssistChipAvatar;
AssistChip.Icon = AssistChipIcon;
AssistChip.Label = AssistChipLabel;
AssistChip.Loading = AssistChipLoading;
